import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { duplicationSelectionEligibility } from "@/lib/ads-manager-duplication";

type Props = {
  brandId: string; accountId: string; activeLevel: string; selectedIds: string[];
  rows: { id: string; name: string; level: string; accountId?: string }[];
  ready: boolean; range?: { since: string; until: string }; buttonLabel?: string;
  onSelectionChange: (ids: string[]) => void;
  blockedReason?: string | null;
  actorId?: string | null;
  buttonSize?: "sm" | "default";
};

class CapabilityError extends Error {
  constructor(public status: number, public code?: string) { super("Capability check failed"); }
}

function capabilityFailure(error: unknown) {
  if (error instanceof CapabilityError) {
    if (error.status === 401) return "Sign in again to check duplication access.";
    if (error.status === 403) return "Editor access to this brand is required to duplicate ads.";
    if (error.code === "EDITABLE_IMPORT_TOOLS_UNAVAILABLE") return "Duplication requires an active brand with Ads Manager and Ad Launcher enabled.";
    if (error.code === "EDITABLE_IMPORT_ACCOUNT_UNAVAILABLE") return "The selected account is not available for import in this brand. Check the current account configuration.";
    if (error.status === 404) return "Duplication is unavailable for this brand or selected account. Check tool and account access.";
  }
  return "Duplication access could not be checked. Try again; nothing has been imported or launched.";
}

export function AdsManagerLauncherAction(props: Props) {
  const [, navigate] = useLocation();
  const { brandId, accountId, selectedIds, rows } = props;
  const capability = useQuery<{ enabled: boolean; maxAds: number }>({
    queryKey: ["/api/brands", brandId, "ads-manager", "launcher-import", "capabilities", accountId, props.actorId],
    enabled: !!brandId && !!accountId && props.activeLevel === "ad" && selectedIds.length > 0,
    staleTime: 0, retry: false, refetchOnMount: "always", refetchOnWindowFocus: "always",
    queryFn: async ({ signal }) => {
      const response = await fetch(`/api/brands/${brandId}/ads-manager/launcher-import/capabilities?accountId=${encodeURIComponent(accountId)}`, { credentials: "include", cache: "no-store", signal });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new CapabilityError(response.status, body?.code);
      }
      const body = await response.json();
      if (typeof body?.enabled !== "boolean" || !Number.isInteger(body?.maxAds) || body.maxAds < 1)
        throw new Error("Invalid capability response");
      return body;
    },
  });
  const eligibility = duplicationSelectionEligibility(accountId, selectedIds, rows, Math.min(capability.data?.maxAds ?? 25, 25));
  if (props.activeLevel !== "ad" || !selectedIds.length || rows.some(row => selectedIds.includes(row.id) && row.level !== "ad")) return null;
  const checking = capability.isFetching || capability.isPending;
  const failure = capability.isError ? capabilityFailure(capability.error)
    : !checking && !capability.data?.enabled ? "Duplication requires Ads Manager and Ad Launcher access for this brand." : null;
  const reason = props.blockedReason ?? eligibility.reason
    ?? (!eligibility.adOnly ? "The selected ads are no longer in the current report. Select the ads again." : null)
    ?? (!accountId ? "Select an authorized account." : null)
    ?? (checking ? "Checking duplication access..." : failure)
    ?? (!props.ready ? "Wait for the current account selection to load." : null)
    ?? (!props.range ? "A reporting range is required to import the selected ads." : null);
  const action = <span className="inline-flex" tabIndex={reason ? 0 : undefined}>
     <Button variant="outline" size={props.buttonSize ?? "sm"} disabled={!!reason} onClick={() => {
      if (reason || !props.range) return;
      const query = new URLSearchParams({
        accountId, metaAdIds: JSON.stringify(selectedIds), importKey: crypto.randomUUID(),
        since: props.range.since, until: props.range.until,
      });
      navigate(`/brands/${encodeURIComponent(brandId)}/ad-launcher?${query}`);
      props.onSelectionChange([]);
    }}><Copy className="mr-1 h-3 w-3" />{props.buttonLabel ?? "Duplicate to new campaign"}</Button>
   </span>;
  return <span className="inline-flex flex-col gap-1">
    {action}
    {reason && <span role={failure ? "alert" : "status"} className="text-xs">
      {reason}
      {failure && !checking && <Button variant="link" size="sm" onClick={() => void capability.refetch()}>Retry access check</Button>}
    </span>}
  </span>;
}
